import { useEffect } from 'react';
import { create } from 'zustand';
import { RealtimeEvents, topics, type Message, type ReactionSummary } from '@tradeoutlet/shared';
import { messageStore } from '@/lib/messageStore';
import { realtime } from '@/lib/realtime';

/** Loads an entity thread's conversation and keeps it live (messages, edits, deletes, reactions) both ways. */
export function useLiveEntityConversation(convId: string | undefined) {
  useEffect(() => {
    if (!convId) return;
    void messageStore.load(convId).then(() => messageStore.catchUp(convId));
    const off = realtime.subscribe(topics.conversation('entity', convId), (event, payload) => {
      switch (event) {
        case RealtimeEvents.MessageCreated: messageStore.ingest(convId, [payload as unknown as Message]); break;
        case RealtimeEvents.MessageUpdated: messageStore.applyUpdate(convId, payload as unknown as Message); break;
        case RealtimeEvents.MessageDeleted: messageStore.applyUpdate(convId, { id: String(payload.id), body: '', deleted_at: String(payload.deleted_at) }); break;
        case RealtimeEvents.ReactionsChanged: messageStore.applyReactions(convId, String(payload.message_id), payload.reactions as ReactionSummary[]); break;
      }
    });
    // Local dev (and reconnects) without live realtime: poll for new messages.
    const t = window.setInterval(() => { if (realtime.getState() !== 'live' && !document.hidden) void messageStore.catchUp(convId); }, 10_000);
    return () => { off(); window.clearInterval(t); };
  }, [convId]);
}

/**
 * Thread host (t157u): outside Chat (Home feed, issue and event pages), "Reply in thread" opens the shared thread
 * panel over the page. Entity-thread lists register which conversation each root message belongs to.
 */
interface Host { convOf: Record<string, string>; info: Record<string, { canPost: boolean }>; add: (convId: string, rootIds: string[], canPost: boolean) => void }
export const useThreadHost = create<Host>(set => ({
  convOf: {}, info: {},
  add: (convId, rootIds, canPost) => set(s => {
    if (rootIds.every(id => s.convOf[id] === convId) && s.info[convId]?.canPost === canPost) return s;
    return { convOf: { ...s.convOf, ...Object.fromEntries(rootIds.map(id => [id, convId])) }, info: { ...s.info, [convId]: { canPost } } };
  }),
}));
